import { createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { STORAGE_KEYS } from "../../config";

const getUserFromStorage = () => {
	try {
		const storedUser = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER));
		if (storedUser && storedUser.token) {
			const decodedToken = jwtDecode(storedUser.token);

			if (decodedToken.exp * 1000 > Date.now()) {
				return {
					...storedUser,
					role: decodedToken.role,
					id: decodedToken.unique_name,
					tokenExpiration: decodedToken.exp * 1000,
				};
			}
		}
		return null;
	} catch (error) {
		console.error("Error parsing user from storage:", error);
		return null;
	}
};

const initialState = {
	user: getUserFromStorage(),
	token: getUserFromStorage()?.token || null,
	isAuthenticated: !!getUserFromStorage(),
	loading: false,
	error: null,
};

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setCredentials: (state, action) => {
			const { token, ...userData } = action.payload;

			const decodedToken = jwtDecode(token);

			const user = {
				...userData,
				token,
				role: decodedToken.role,
				id: decodedToken.unique_name,
				tokenExpiration: decodedToken.exp * 1000,
			};

			state.user = user;
			state.token = token;
			state.isAuthenticated = true;
			state.error = null;

			localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

			localStorage.setItem(
				STORAGE_KEYS.TOKEN_EXPIRATION,
				decodedToken.exp * 1000
			);
		},
		logOut: (state) => {
			state.user = null;
			state.token = null;
			state.isAuthenticated = false;

			localStorage.clear();

			sessionStorage.clear();

			document.cookie.split(";").forEach((cookie) => {
				const eqPos = cookie.indexOf("=");
				const name =
					eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
			});

			window.location.reload();
		},
		setError: (state, action) => {
			state.error = action.payload;
			state.loading = false;
		},
		clearError: (state) => {
			state.error = null;
		},
		setLoading: (state, action) => {
			state.loading = action.payload;
		},
	},
});

export const { setCredentials, logOut, setError, clearError, setLoading } =
	authSlice.actions;

export const checkTokenExpiration = () => (dispatch) => {
	const expirationTime = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRATION);
	if (expirationTime) {
		const timeLeft = parseInt(expirationTime) - Date.now();
		if (timeLeft <= 0) {
			dispatch(logOut());
		} else {
			setTimeout(() => {
				dispatch(logOut());
			}, timeLeft);
		}
	}
};

export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
